/**
 * ContactSection — eyebrow + heading + lead + form labels + subject options
 * + form state messages + 4 contact-info cards.
 *
 * Paths covered:
 *   contact.{eyebrow, heading, lead}
 *   contact.form.{nameLabel, namePlaceholder, emailLabel, emailPlaceholder,
 *                 subjectLabel, messageLabel, messagePlaceholder,
 *                 subjects.{general,support,bug,suggestion,sales},
 *                 submit, submitting, success, errorSend, errorNetwork}
 *   contact.cards.{emailTitle, phoneTitle, whatsappTitle, chatTitle, chatBody}
 *
 * Contact info values (email, phone, whatsapp number) live under brand.*
 * because they're language-agnostic and shared with the footer.
 */

import {
  BilingualTextField, BilingualTextareaField,
  SectionPanel, UrlField,
} from '../fields/BilingualFields'

export default function ContactSection({ getField, setField }) {
  const emailAddr = getField('en', 'brand.contactEmail', '') || 'info@ovacs.com'
  const phone = getField('en', 'brand.contactPhone', '') || '+971 54 235 8420'
  const whatsapp = getField('en', 'brand.contactWhatsapp', '') || '+971 54 235 8420'
  const whatsappLink = getField('en', 'brand.whatsappLink', '') || 'https://wa.me/971542358420'

  const setBoth = (key, val) => {
    setField('en', key, val)
    setField('ar', key, val)
  }

  return (
    <SectionPanel title="Contact" subtitle="Form labels + subjects + info cards">
      <BilingualTextField label="Eyebrow" path="contact.eyebrow" getField={getField} setField={setField} />
      <BilingualTextField label="Section heading" path="contact.heading" getField={getField} setField={setField} />
      <BilingualTextareaField label="Lead paragraph" path="contact.lead" getField={getField} setField={setField} rows={2} />

      <hr className="my-3 border-gray-200" />
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Form labels & placeholders</h4>
      <BilingualTextField label="Name label" path="contact.form.nameLabel" getField={getField} setField={setField} />
      <BilingualTextField label="Name placeholder" path="contact.form.namePlaceholder" getField={getField} setField={setField} />
      <BilingualTextField label="Email label" path="contact.form.emailLabel" getField={getField} setField={setField} />
      <BilingualTextField label="Email placeholder" path="contact.form.emailPlaceholder" getField={getField} setField={setField} />
      <BilingualTextField label="Subject label" path="contact.form.subjectLabel" getField={getField} setField={setField} />
      <BilingualTextField label="Message label" path="contact.form.messageLabel" getField={getField} setField={setField} />
      <BilingualTextField label="Message placeholder" path="contact.form.messagePlaceholder" getField={getField} setField={setField} />

      <hr className="my-3 border-gray-200" />
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Subject options (dropdown)</h4>
      <BilingualTextField label="General inquiry" path="contact.form.subjects.general" getField={getField} setField={setField} />
      <BilingualTextField label="Technical support" path="contact.form.subjects.support" getField={getField} setField={setField} />
      <BilingualTextField label="Report a bug" path="contact.form.subjects.bug" getField={getField} setField={setField} />
      <BilingualTextField label="Feature suggestion" path="contact.form.subjects.suggestion" getField={getField} setField={setField} />
      <BilingualTextField label="Sales & pricing" path="contact.form.subjects.sales" getField={getField} setField={setField} />

      <hr className="my-3 border-gray-200" />
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Submit button + state messages</h4>
      <BilingualTextField label="Submit button label" path="contact.form.submit" getField={getField} setField={setField} />
      <BilingualTextField label="Submitting label" path="contact.form.submitting" getField={getField} setField={setField} />
      <BilingualTextField label="Success message" path="contact.form.success" getField={getField} setField={setField} />
      <BilingualTextField label="Send-error message" path="contact.form.errorSend" getField={getField} setField={setField} />
      <BilingualTextField label="Network-error message" path="contact.form.errorNetwork" getField={getField} setField={setField} />

      <hr className="my-3 border-gray-200" />
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Contact info cards (below form)</h4>
      <BilingualTextField label='"Email us" card title' path="contact.cards.emailTitle" getField={getField} setField={setField} />
      <BilingualTextField label='"Call us" card title' path="contact.cards.phoneTitle" getField={getField} setField={setField} />
      <BilingualTextField label='"WhatsApp" card title' path="contact.cards.whatsappTitle" getField={getField} setField={setField} />
      <BilingualTextField label='"Live Chat" card title' path="contact.cards.chatTitle" getField={getField} setField={setField} />
      <BilingualTextField label='"Live Chat" card body (e.g. "Available 24/7")' path="contact.cards.chatBody" getField={getField} setField={setField} />

      <hr className="my-3 border-gray-200" />
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Contact info values (language-agnostic)</h4>
      <UrlField
        label="Email address"
        value={emailAddr}
        onChange={(v) => setBoth('brand.contactEmail', v)}
        placeholder="info@ovacs.com"
      />
      <UrlField
        label="Phone number (displayed)"
        value={phone}
        onChange={(v) => setBoth('brand.contactPhone', v)}
        placeholder="+971 54 235 8420"
      />
      <UrlField
        label="WhatsApp number (displayed)"
        value={whatsapp}
        onChange={(v) => setBoth('brand.contactWhatsapp', v)}
        placeholder="+971 54 235 8420"
      />
      <UrlField
        label="WhatsApp link (clickable wa.me/...)"
        value={whatsappLink}
        onChange={(v) => setBoth('brand.whatsappLink', v)}
        placeholder="https://wa.me/971542358420"
      />
    </SectionPanel>
  )
}
